/**
 * @file Renders ONE declared column of a row projection as the phrase the
 * drawer's column list and the admin list's expanded row both show — one
 * spelling, so the two can never word an arm two ways. Consumers: the Derived
 * values drawer's per-column rows and `describeFormDeriveSource`'s siblings.
 * Pure: `labels` supplies every lookup, and an id it cannot place renders raw.
 * Invariants: the phrase never names the source table, because every row-value
 * arm reads the entry's ONE source table and the entry sentence already names
 * it; `›` (U+203A) separates a reference column from the property behind it; and
 * the secondary facts attach with ` · `, rounding first and live reads second,
 * the order `describeFormDeriveSource` fixes for the whole subsystem.
 */
import type {
  ArColumnAggregation,
  ArTableId,
  FormDeriveReferenceProp,
  FormDeriveRowValueSource,
} from '#types';
import ArColumnAggregationOptions from '@/components/settings/ArTableEditorTab/ArColumnAggregationOptions';
import { formDeriveCopy } from './formDeriveCopy';
import type { FormDeriveLabelResolvers } from './formDeriveLabelResolvers';

/** The same options the drawer's Aggregation select renders, so neither can drift. */
function aggregationLabel(aggregation: ArColumnAggregation): string {
  return (
    ArColumnAggregationOptions.find((option) => option.value === aggregation)?.label ?? aggregation
  );
}

function propLabel(prop: FormDeriveReferenceProp, labels: FormDeriveLabelResolvers): string {
  if (prop.kind === 'name') return formDeriveCopy.referencePropName;
  if (prop.kind === 'field') return labels.fieldLabel(prop.fieldId) ?? prop.fieldId;
  return labels.referenceLabel(prop.referenceKey) ?? prop.referenceKey;
}

function describeFormDeriveRowValue(
  value: FormDeriveRowValueSource,
  sourceTableId: ArTableId,
  labels: FormDeriveLabelResolvers,
): string {
  if (value.kind === 'groupKey') return formDeriveCopy.rowValueKindOption.groupKey;

  if (value.kind === 'cases') {
    const count = value.cases.length;
    return `${count} ${count === 1 ? 'case' : 'cases'}, first match wins`;
  }

  if (value.kind === 'rowReferenceProp') {
    const column =
      labels.columnLabel(sourceTableId, value.referenceTypeId) ?? value.referenceTypeId;
    return `${column} › ${propLabel(value.prop, labels)} · read live`;
  }

  if (value.kind === 'groupAggregate') {
    const typeId =
      value.aggregationFieldPath === undefined
        ? value.typeId
        : `${value.typeId}.${value.aggregationFieldPath}`;
    const column = labels.columnLabel(sourceTableId, typeId) ?? typeId;
    const rounding = value.roundToCents ? ', rounded to cents' : '';
    return `${aggregationLabel(value.aggregation)} of ${column}${rounding}`;
  }

  return labels.columnLabel(sourceTableId, value.typeId) ?? value.typeId;
}

export { describeFormDeriveRowValue };
